/**
 * Text-to-SOQL Service
 *
 * REST client for the Text-to-SOQL graph service (generate SOQL from natural language,
 * execute SOQL, and request corrections). Uses the same base URL as the documentation
 * microservice (soql-graph-service).
 *
 * APIs:
 *   POST /v2/agent/chat — generate or correct SOQL
 *   POST /soql/execute  — execute SOQL against Salesforce
 */

import { createAxiosInstance } from '@/dataProvider/axios';
import { env } from '@/config/env';
import type {
  AgentChatInput,
  AgentChatResponse,
  SoqlExecuteResponse,
} from './types';

export * from './types';

// ============================================================================
// Base URL — routed through backend proxy which injects GCP OIDC token
// ============================================================================

const textToSoqlAxios = createAxiosInstance(`${env.API_URL}/proxy/graph`);

// ============================================================================
// Org headers (X-Instance-URL, X-Organization-ID, X-User-ID)
// ============================================================================

export type OrgLike = { instanceUrl?: string; id?: string } | null | undefined;

function orgHeaders(org: OrgLike, userId?: string | null): Record<string, string> {
  const headers: Record<string, string> = {};
  if (org?.instanceUrl) headers['X-Instance-URL'] = org.instanceUrl;
  if (org?.id) headers['X-Organization-ID'] = org.id;
  if (userId) headers['X-User-ID'] = userId;
  return headers;
}

// ============================================================================
// Service functions
// ============================================================================

/**
 * Generate or correct SOQL via the agent chat API.
 *
 * @param input  - { message, session_id }
 * @param org    - Selected org (instanceUrl + id sent as headers)
 * @param userId - Current user's UUID (sent as X-User-ID; required by
 *                 ncoder-backend's per-user Salesforce session lookup)
 * @returns Agent response with query, explanation, or error
 */
export async function agentChat(
  input: AgentChatInput,
  org: OrgLike,
  userId?: string | null,
): Promise<AgentChatResponse> {
  const { data } = await textToSoqlAxios.post<AgentChatResponse>(
    '/v2/agent/chat',
    { ...input, org_id: org?.id },
    { headers: orgHeaders(org, userId) },
  );
  return data;
}

/**
 * Execute SOQL against Salesforce.
 *
 * @param query  - SOQL string to execute
 * @param org    - Selected org (instanceUrl + id sent as headers)
 * @param userId - Current user's UUID (sent as X-User-ID; required by
 *                 ncoder-backend's per-user Salesforce session lookup)
 * @returns Success with records + totalSize, or error object
 */
export async function executeSoql(
  query: string,
  org: OrgLike,
  userId?: string | null,
): Promise<SoqlExecuteResponse> {
  const { data } = await textToSoqlAxios.post<SoqlExecuteResponse>(
    '/soql/execute',
    { query, org_id: org?.id },
    { headers: orgHeaders(org, userId) },
  );
  return data;
}
